// ======================================
// About Page (Dynamic)
// ======================================

import { icons } from '../components.js';

import { aboutAPI, teamAPI } from '../lib/api.js';

export function renderAboutPage() {
    return `
    <div id="about-content" class="fade-in">
        <!-- Hero Section -->
        <section class="page-header">
            <div class="container">
                <div class="page-header-content">
                    <h1 class="page-title skeleton-text" style="width:60%;">Loading...</h1>
                    <p class="page-subtitle skeleton-text" style="width:80%;">Loading...</p>
                </div>
            </div>
        </section>

        <!-- Loading Handler -->
        <div class="container" style="padding:var(--space-8) 0; text-align:center;">
            <div class="loading-spinner"></div>
        </div>
    </div>
  `;
}

export async function initAboutPage() {
    const container = document.getElementById('about-content');
    if (!container) return;

    try {
        const response = await aboutAPI.get();
        const data = response.data || {};

        // Update Meta Tags (Title/Desc)
        if (data.meta_title) document.title = data.meta_title;
        if (data.meta_description) {
            const metaDesc = document.querySelector('meta[name="description"]');
            if (metaDesc) metaDesc.setAttribute('content', data.meta_description);
        }

        const heroTitle = data.hero_title || 'Built on Trust. Designed for Homes.';
        const heroSubtitle = data.hero_subtitle || '';

        // Mission & Vision
        const mission = data.mission || 'To make premium interior design accessible and transparent for every homeowner.';
        const vision = data.vision || 'To be the most trusted interior brand in South India known for quality and timely delivery.';

        // Founder
        const founderName = data.founder_name || 'Sivakumar';
        const founderMsg = data.founder_message || '';
        const founderImg = data.founder_image || ''; // Placeholder if empty handled in CSS/HTML

        // Stats
        const expYears = data.years_experience || 8;
        const projects = data.projects_completed || 320;
        const warranty = data.warranty_years || 10;

        // Factory
        const factoryInfo = data.factory_info || '';

        // Render Full Content
        container.innerHTML = `
        <!-- Hero Section -->
        <section class="page-header">
            <div class="container">
                <div class="page-header-content">
                    <h1 class="page-title">${heroTitle}</h1>
                    <p class="page-subtitle">${heroSubtitle}</p>
                </div>
            </div>
        </section>

        <!-- Mission & Vision -->
        <section class="section">
            <div class="container">
                <div style="display:grid;grid-template-columns:repeat(auto-fit, minmax(300px, 1fr));gap:var(--space-8);">
                    <div class="info-card">
                        <div class="info-icon" style="font-size:32px;margin-bottom:var(--space-4);">🎯</div>
                        <h3 class="info-title">Our Mission</h3>
                        <p class="info-text">${mission}</p>
                    </div>
                    <div class="info-card">
                        <div class="info-icon" style="font-size:32px;margin-bottom:var(--space-4);">👁️</div>
                        <h3 class="info-title">Our Vision</h3>
                        <p class="info-text">${vision}</p>
                    </div>
                </div>
            </div>
        </section>

        <!-- Stats Section -->
        <section class="section bg-light">
            <div class="container">
                <div class="stats-grid">
                    <div class="stat-item">
                        <span class="stat-number">${projects}+</span>
                        <span class="stat-label">Projects Completed</span>
                    </div>
                    <div class="stat-item">
                        <span class="stat-number">${expYears}+</span>
                        <span class="stat-label">Years Experience</span>
                    </div>
                    <div class="stat-item">
                        <span class="stat-number">${warranty}</span>
                        <span class="stat-label">Year Warranty</span>
                    </div>
                </div>
            </div>
        </section>

        <!-- Founder Section -->
        <section class="section">
            <div class="container">
                <div class="founder-section" style="display:flex;align-items:center;gap:var(--space-8);flex-wrap:wrap;">
                    <div class="founder-image" style="flex:1;min-width:300px;">
                        ${founderImg
                ? `<img src="${founderImg}" alt="${founderName}" style="width:100%;border-radius:var(--radius-lg);box-shadow:var(--shadow-lg);">`
                : `<div style="width:100%;height:300px;background:var(--color-surface-warm);border-radius:var(--radius-lg);display:flex;align-items:center;justify-content:center;color:var(--color-text-muted);">No Founder Image</div>`
            }
                    </div>
                    <div class="founder-content" style="flex:1;min-width:300px;">
                        <h2 class="section-title">Message from the Founder</h2>
                        <div class="founder-quote">
                            <blockquote style="font-size:18px;font-style:italic;line-height:1.6;margin-bottom:var(--space-4);border-left:4px solid var(--color-primary);padding-left:var(--space-4);">
                                "${founderMsg}"
                            </blockquote>
                            <cite style="display:block;font-weight:700;color:var(--color-primary);font-style:normal;">
                                — ${founderName}
                            </cite>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Factory Section -->
        ${factoryInfo ? `
        <section class="section bg-light">
            <div class="container">
                <h2 class="section-title" style="text-align:center;">Factory & Execution</h2>
                <div class="factory-content" style="max-width:800px;margin:0 auto;text-align:center;">
                    <p>${factoryInfo}</p>
                </div>
            </div>
        </section>
        ` : ''}

        <!-- Team Section -->
        <section class="section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">Meet Our Team</h2>
                    <p class="section-subtitle">The creative minds behind your dream homes.</p>
                </div>
                <div id="team-grid" class="team-grid" style="display: grid; grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); gap: var(--space-8);">
                    <div class="loading-spinner"></div>
                </div>
            </div>
        </section>
        
        <!-- CTA -->
        <section class="section cta-section">
            <div class="container">
                <div class="cta-content">
                    <h2 class="cta-title">Ready to Transform Your Home?</h2>
                    <p class="cta-subtitle">Let's create something beautiful together.</p>
                    <button class="btn btn-lg cta-btn" data-open-modal="lead-modal">Get Free Consultation</button>
                </div>
            </div>
        </section>
            </div>
        </section>
    `;

        // Fetch Team Members
        try {
            const teamResponse = await teamAPI.list();
            const teamMembers = Array.isArray(teamResponse) ? teamResponse : (teamResponse.data || []);
            const teamGrid = document.getElementById('team-grid');

            if (teamGrid) {
                if (teamMembers.length > 0) {
                    teamGrid.innerHTML = teamMembers.map(member => `
                        <div class="team-card" style="background:var(--color-surface);border-radius:var(--radius-lg);overflow:hidden;box-shadow:var(--shadow-sm);transition:transform 0.3s ease;">
                            <div class="team-img-wrapper" style="aspect-ratio:3/4;overflow:hidden;">
                                <img src="${member.image || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400'}" alt="${member.name}" style="width:100%;height:100%;object-fit:cover;transition:transform 0.5s ease;">
                            </div>
                            <div class="team-info" style="padding:var(--space-4);text-align:center;">
                                <h3 class="team-name" style="font-size:18px;font-weight:700;margin-bottom:4px;">${member.name}</h3>
                                <p class="team-role" style="color:var(--color-primary);font-size:14px;font-weight:500;margin-bottom:var(--space-2);">${member.role}</p>
                                ${member.bio ? `<p class="team-bio" style="font-size:13px;color:var(--color-text-muted);line-height:1.4;">${member.bio}</p>` : ''}
                            </div>
                        </div>
                    `).join('');
                } else {
                    teamGrid.innerHTML = '<p style="text-align:center;grid-column:1/-1;">Team members coming soon.</p>';
                }
            }
        } catch (e) {
            console.error('Failed to load team:', e);
            document.getElementById('team-grid').innerHTML = ''; // Hide loader
        }

        // Re-attach modal listeners if needed? 
        // Usually handled by global listener on document, but if not:
        // This is SPA, main.js usually waits for content to load? 
        // Actually initAboutPage runs AFTER render.
        // If global listeners are on 'document', they delegate. Correct.

    } catch (error) {
        console.error('Failed to load about page', error);
        container.innerHTML = '<div class="error-message">Failed to load content.</div>';
    }
}
